import React from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { CustomTable, SelectColumnFilter } from '../../common/tables';
import commafy from 'commafy';

const formatAmount = (amount) => {
  if (amount == null || amount === '' || isNaN(parseFloat(amount))) {
    return null;
  }
  return parseFloat(amount).toFixed(2);
};

const StyledTableWrapper = styled(Box)`
  width: 100%;
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #e5e7eb;
  }

  thead {
    background: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
  }

  th {
    color: #6b7280;
    text-align: left;
    padding: 1rem 1.5rem;
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    border-right: 1px solid #e5e7eb;

    &:last-child {
      border-right: none;
    }
  }

  tbody tr {
    border-bottom: 1px solid #e5e7eb;
    transition: background-color 0.2s;

    &:hover {
      background: #f9fafb;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  td {
    padding: 1.25rem 1.5rem;
    color: #374151;
    font-size: 14px;
    white-space: nowrap;
    border-right: 1px solid #e5e7eb;

    &:last-child {
      border-right: none;
    }
  }
`;

const TransactionsTable = ({ transactions }) => {
  if (!transactions || transactions.length === 0) {
    return (
      <Box bg="white" p="2rem" borderRadius="8px" mb="2rem" boxShadow="md">
        <Text fontSize="20px" fontFamily="bold" color="brand.header" mb="1rem">
          Transactions
        </Text>
        <Box
          p="3rem"
          bg="#F9FAFB"
          borderRadius="8px"
          textAlign="center"
          border="1px dashed #D1D5DB"
        >
          <Text fontSize="16px" color="#6B7280">
            No transactions found for this period.
          </Text>
        </Box>
      </Box>
    );
  }

  const defaultData = transactions.map((item) => ({
    col1: item.reference,
    col2: item.phone_number,
    col3: item.product_name,
    col4: item.product_type,
    col5: item.amount,
    col6: item.debit_amount,
    col7: item.balance_before,
    col8: item.balance_after,
    col9: item.status,
    col10: item.transaction_date,
    col11: item.response,
  }));

  const defaultColumns = [
    {
      Header: 'Reference',
      accessor: 'col1',
      disableSortBy: true,
      disableFilters: true,
    },
    {
      Header: 'Phone Number',
      accessor: 'col2',
      disableSortBy: true,
      disableFilters: true,
    },
    {
      Header: 'Product',
      accessor: 'col3',
      Cell: ({ value }) => (
        <Text fontFamily="medium" textTransform="uppercase">
          {value}
        </Text>
      ),
      disableFilters: true,
    },
    {
      Header: 'Type',
      accessor: 'col4',
      Cell: ({ value }) => (
        <Text fontFamily="medium" textTransform="capitalize">
          {value}
        </Text>
      ),
      Filter: SelectColumnFilter,
      disableSortBy: true,
    },
    {
      Header: 'Amount',
      accessor: 'col5',
      Cell: ({ value }) => {
        const formatted = formatAmount(value);
        return (
          <Text fontFamily="medium">
            {formatted != null ? `₦${commafy(formatted)}` : 'N/A'}
          </Text>
        );
      },
      disableFilters: true,
    },
    {
      Header: 'Debit Amount',
      accessor: 'col6',
      Cell: ({ value }) => {
        const formatted = formatAmount(value);
        return (
          <Text fontFamily="medium">
            {formatted != null ? `₦${commafy(formatted)}` : 'N/A'}
          </Text>
        );
      },
      disableFilters: true,
    },
    {
      Header: 'Balance Before',
      accessor: 'col7',
      Cell: ({ value }) => {
        const formatted = formatAmount(value);
        return (
          <Text fontFamily="medium">
            {formatted != null ? `₦${commafy(formatted)}` : 'N/A'}
          </Text>
        );
      },
      disableFilters: true,
    },
    {
      Header: 'Balance After',
      accessor: 'col8',
      Cell: ({ value }) => {
        const formatted = formatAmount(value);
        return (
          <Text fontFamily="medium">
            {formatted != null ? `₦${commafy(formatted)}` : 'N/A'}
          </Text>
        );
      },
      disableFilters: true,
    },
    {
      Header: 'Status',
      accessor: 'col9',
      Cell: ({ value }) => (
        <Text
          fontFamily="medium"
          color={
            value === 'successful'
              ? '#2EAE65'
              : value === 'pending'
              ? '#FFA500'
              : '#E24D4D'
          }
          textTransform="capitalize"
        >
          {value}
        </Text>
      ),
      Filter: SelectColumnFilter,
      disableSortBy: true,
    },
    {
      Header: 'Date',
      accessor: 'col10',
      Cell: ({ value }) => (
        <Text fontFamily="regular">
          {dayjs(value).format('MMM D, YYYY')}
          <Text as="span" ml="10px" color="#808080">
            {dayjs(value).format('hh:mm A')}
          </Text>
        </Text>
      ),
      disableFilters: true,
    },
  ];

  return (
    <Box bg="white" p="2rem" borderRadius="8px" mb="2rem" boxShadow="md">
      <Flex justifyContent="space-between" alignItems="center" mb="2rem">
        <Text fontSize="20px" fontFamily="bold" color="brand.header">
          Transactions
        </Text>
        <Box
          bg="#EBF4FF"
          px="1rem"
          py="0.5rem"
          borderRadius="20px"
          border="1px solid"
          borderColor="brand.blue"
        >
          <Text fontSize="14px" fontFamily="bold" color="brand.blue">
            {transactions.length}{' '}
            {transactions.length === 1 ? 'Transaction' : 'Transactions'}
          </Text>
        </Box>
      </Flex>
      <StyledTableWrapper>
        <CustomTable
          columns={defaultColumns}
          data={defaultData}
          showPagination={false}
        />
      </StyledTableWrapper>
    </Box>
  );
};

export { TransactionsTable };
